import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Mic, MicOff, Upload, Play, Square, RefreshCw, Sparkles, AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoiceInspection = ({ open, onClose, onDataParsed, token, userLocation }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('record'); // record, review, transcribe, parse
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const resetState = () => {
    setIsRecording(false);
    setRecordingTime(0);
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setTranscript('');
    setIsTranscribing(false);
    setIsParsing(false);
    setError(null);
    setStep('record');
    audioChunksRef.current = [];
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Determine the best supported MIME type for this browser/device
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/wav',
        ''  // Let browser choose default
      ];
      
      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (mimeType === '' || MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }
      
      
      const mediaRecorder = selectedMimeType 
        ? new MediaRecorder(stream, { mimeType: selectedMimeType })
        : new MediaRecorder(stream);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        // Use the actual mimeType from the recorder
        const actualMimeType = mediaRecorder.mimeType || 'audio/webm';
        
        const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setStep('review');
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access to record.');
      } else {
        setError('Failed to start recording. Please check your microphone.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Be lenient with file types - accept any audio or video
    const fileType = file.type || '';
    const isAudioVideo = (
      fileType.startsWith('audio/') ||
      fileType.startsWith('video/') ||
      fileType.includes('audio') ||
      fileType.includes('video') ||
      // Check file extension as fallback
      /\.(mp3|wav|m4a|mp4|webm|ogg|aac|flac|3gp|mov)$/i.test(file.name)
    );
    
    if (!isAudioVideo && fileType) {
      setError('Please upload an audio or video file (MP3, WAV, M4A, MP4, WebM, etc.)');
      return;
    }
    
    // Validate file size (25MB)
    if (file.size > 25 * 1024 * 1024) {
      setError('File too large. Maximum size is 25MB.');
      return;
    }
    
    
    setError(null);
    setAudioBlob(file);
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    setStep('review');
  };

  const transcribeAudio = async () => {
    if (!audioBlob) {
      setError('No audio recording found. Please record or upload audio first.');
      return;
    }
    
    if (!token) {
      setError('Session expired. Please refresh the page and login again.');
      return;
    }
    
    setIsTranscribing(true);
    setError(null);
    
    try {
      const formData = new FormData();
      
      // Determine file extension based on MIME type
      const mimeType = audioBlob.type || 'audio/webm';
      let extension = 'webm';
      if (mimeType.includes('mp4') || mimeType.includes('m4a')) extension = 'mp4';
      else if (mimeType.includes('ogg')) extension = 'ogg';
      else if (mimeType.includes('wav')) extension = 'wav';
      else if (mimeType.includes('mp3') || mimeType.includes('mpeg')) extension = 'mp3';
      else if (mimeType.includes('aac')) extension = 'aac';
      else if (mimeType.includes('flac')) extension = 'flac';
      
      const filename = audioBlob.name || `recording.${extension}`;
      
      formData.append('file', audioBlob, filename);
      
      const response = await axios.post(`${API}/inspections/transcribe-voice`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setTranscript(response.data.transcript);
      setStep('transcribe');
    } catch (err) {
      
      let errorMessage = 'Failed to transcribe audio. Please try again.';
      if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = 'Session expired. Please refresh the page and try again.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setIsTranscribing(false);
    }
  };

  const parseTranscript = async () => {
    if (!transcript) {
      setError('No transcript available. Please transcribe audio first.');
      return;
    }
    
    if (!token) {
      setError('Session expired. Please refresh the page and login again.');
      return;
    }
    
    setIsParsing(true);
    setError(null);
    
    try {
      // First, get weather data if location is available
      let weatherData = null;
      if (userLocation?.lat && userLocation?.lng) {
        try {
          const weatherRes = await axios.get(
            `${API}/weather/current?lat=${userLocation.lat}&lng=${userLocation.lng}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          weatherData = weatherRes.data;
        } catch (e) {
        }
      }
      
      // Parse the transcript
      const response = await axios.post(`${API}/inspections/parse-transcript`, 
        { transcript },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const parsedData = response.data.parsed_data || {};
      
      // Add date, time, and weather
      const now = new Date();
      const enrichedData = {
        ...parsedData,
        date: now.toISOString().split('T')[0],
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        transcript: transcript,
        ...(weatherData && !weatherData.error ? {
          temperature: weatherData.temperature?.toString() || '',
          humidity: weatherData.humidity?.toString() || '',
          wind: weatherData.wind_speed?.toString() || '',
          weather_conditions: weatherData.conditions || ''
        } : {})
      };
      
      setStep('parse');
      
      // Pass data back to parent
      setTimeout(() => {
        onDataParsed(enrichedData);
        onClose();
      }, 1500);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to parse transcript. Please try again.');
    } finally {
      setIsParsing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={() => { if (!isRecording && !isTranscribing && !isParsing) onClose(); }}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-stone-800 font-heading flex items-center gap-2">
            <Mic className="h-6 w-6 text-amber-600" />
            Voice Hive Inspection
          </DialogTitle>
        </DialogHeader>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 py-4">
          {['record', 'review', 'transcribe', 'parse'].map((s, i) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === s ? 'bg-amber-500 text-white' :
                ['record', 'review', 'transcribe', 'parse'].indexOf(step) > i ? 'bg-green-500 text-white' :
                'bg-stone-200 text-stone-500'
              }`}>
                {['record', 'review', 'transcribe', 'parse'].indexOf(step) > i ? '✓' : i + 1}
              </div>
              {i < 3 && <div className={`w-8 h-0.5 ${['record', 'review', 'transcribe', 'parse'].indexOf(step) > i ? 'bg-green-500' : 'bg-stone-200'}`} />}
            </React.Fragment>
          ))}
        </div>
        
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        
        {/* Step: Record */}
        {step === 'record' && (
          <div className="space-y-6">
            <p className="text-sm text-stone-600 text-center">
              Record your inspection observations or upload an audio file.
              Speak naturally about what you see - the AI will extract the details.
            </p>
            
            {/* Recording UI */}
            <div className="flex flex-col items-center gap-4">
              {isRecording ? (
                <>
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center animate-pulse">
                      <Mic className="h-10 w-10 text-red-600" />
                    </div>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-red-600 text-white text-sm font-mono px-3 py-1 rounded-full">
                      {formatTime(recordingTime)}
                    </div>
                  </div>
                  <Button
                    onClick={stopRecording}
                    className="bg-red-600 hover:bg-red-700 text-white px-6"
                  >
                    <Square className="h-4 w-4 mr-2" />
                    Stop Recording
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={startRecording}
                    className="w-24 h-24 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-700"
                  >
                    <Mic className="h-10 w-10" />
                  </Button>
                  <span className="text-sm text-stone-500">Tap to start recording</span>
                </>
              )}
            </div>
            
            {/* Divider */}
            {!isRecording && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-stone-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-stone-500">or</span>
                  </div>
                </div>
                
                {/* Upload Button */}
                <div className="flex justify-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*,video/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full max-w-xs"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Audio/Video File
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
        
        {/* Step: Review */}
        {step === 'review' && audioUrl && (
          <div className="space-y-6">
            <p className="text-sm text-stone-600 text-center">
              Review your recording before transcription.
            </p>
            
            <div className="bg-stone-50 rounded-lg p-4">
              <audio src={audioUrl} controls className="w-full" />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={resetState}
                className="flex-1"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Re-record
              </Button>
              <Button
                onClick={transcribeAudio}
                disabled={isTranscribing}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                {isTranscribing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Transcribing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Transcribe
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
        
        {/* Step: Transcribe */}
        {step === 'transcribe' && (
          <div className="space-y-6">
            <p className="text-sm text-stone-600 text-center">
              Review the transcript. You can edit it before parsing.
            </p>
            
            <div className="bg-stone-50 rounded-lg p-4">
              <textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                className="w-full h-40 bg-transparent border-none resize-none focus:outline-none text-sm text-stone-700"
                placeholder="Transcript will appear here..."
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={resetState}
                className="flex-1"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Start Over
              </Button>
              <Button
                onClick={parseTranscript}
                disabled={isParsing || !transcript}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isParsing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Fill Form with AI
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
        
        {/* Step: Parse Complete */}
        {step === 'parse' && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Form Data Extracted!</h3>
            <p className="text-sm text-stone-500 text-center">
              Populating your inspection form with the extracted data...
            </p>
          </div>
        )}
        
        {/* Close Button (only when not in progress) */}
        {!isRecording && !isTranscribing && !isParsing && step !== 'parse' && (
          <div className="pt-4 border-t border-stone-200">
            <Button variant="secondary" onClick={onClose} className="w-full">
              Cancel
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VoiceInspection;
